import { useRESTClient } from 'context';

export interface AiChatUploadInput {
  prompt: string;
  files?: File[];
  newConversation?: boolean;
}

export async function uploadAiChatWithFiles({
  axios,
  input,
}: {
  axios: any;
  input: AiChatUploadInput;
}) {
  const { prompt, files = [], newConversation = false } = input;

  try {
    // If no files, use regular GraphQL mutation
    if (files.length === 0) {
      return await axios.post('graphql', {
        query: `mutation AiChat($input: ChatInput!) {
          aiChat(input: $input) {
            response
          }
        }`,
        variables: {
          input: {
            prompt,
            newConversation,
          },
        },
      });
    }

    // For file uploads, use multipart form data
    const mutationInput: Record<string, unknown> = {
      prompt,
      newConversation,
    };
    if (files.length > 0) {
      mutationInput.files = files.map(() => null); // Placeholder for file references
    }
    const mutation = {
      query: `mutation AiChat($input: ChatInput!) {
        aiChat(input: $input) {
          response
        }
      }`,
      variables: {
        input: mutationInput,
      },
    };

    // Create the map for file references
    const map: Record<string, string[]> = {};
    files.forEach((_, index) => {
      map[index.toString()] = [`variables.input.files.${index}`];
    });

    const formData = new FormData();
    formData.append('operations', JSON.stringify(mutation));
    formData.append('map', JSON.stringify(map));

    // Append each file
    files.forEach((file, index) => {
      formData.append(index.toString(), file, file.name);
    });

    return await axios({
      url: 'graphql',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  } catch (error) {
    console.error('Error uploading AI chat with files:', error);

    // Re-throw with more context
    if (error instanceof Error) {
      throw new Error(`Failed to upload AI chat: ${error.message}`);
    }

    throw new Error('Failed to upload AI chat: Unknown error occurred');
  }
}

export function useAiChatUploader() {
  const { axios } = useRESTClient();

  return {
    uploadAiChatWithFiles: (input: AiChatUploadInput) =>
      uploadAiChatWithFiles({ axios, input }),
  };
}
